import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Bookmark } from 'lucide-react';

import { Button } from '@/components/common/Button';
import { PanelWaiting } from '@/components/hub/PanelNotice';
import { BoardRiga } from '@/components/myBoards/BoardRiga';
import { BoardScheda } from '@/components/myBoards/BoardScheda';
import { SfidaDaBoardSheet } from '@/components/myBoards/SfidaDaBoardSheet';
import { useBoardDettaglio, useMyBoards, useSfidaDaBoard, useTogliBoard } from '@/hooks/useMyBoards';
import { sfidaDaBoardAcceso } from '@/lib/backgammon/myBoardsAcceso';
import { useHaptics } from '@/hooks/useHaptics';
import { useLivelloIndietro } from '@/hooks/useLivelloIndietro';
import { FILTRI_BOARD, type FiltroBoard } from '@/lib/myBoards/riga';
import type { ErroreBoard } from '@/lib/myBoards/errori';
import type { BoardView, ScenarioView } from '@/types/myBoards';

/**
 * La scheda «My Boards» del backgammon (ADR-0191; tavole T4-T6): la LISTA dei board salvati — privata, il piu' recente in
 * cima, con i filtri per provenienza (Giocati, Guardati, Replay) — e, aperta una riga, la SCHEDA del board con
 * «Sfida un amico da qui». Si monta dall'elenco unico delle schede (`lobbySections.ts`), a predicato acceso
 * (`myBoardsAcceso`), in Hub e in `/backgammon` insieme.
 *
 * T1: dati VERI (`useMyBoards`: `GET /boards` a cursore, filtro lato server), flag spenta in ogni file env. «Togli» e' nella
 * scheda. «Salva» (la cattura in partita, replay e spettatore) e' di `lib/myBoards/salva.ts`, la sfida vera e' il T2.
 * La scheda aperta e' uno stato di QUESTO pannello (non una rotta): il tasto indietro di Telegram torna alla lista
 * (`useLivelloIndietro`), come un foglio.
 */
export function MyBoardsPanel() {
  const { t } = useTranslation('backgammon');
  const haptics = useHaptics();
  const navigate = useNavigate();
  const [filtro, setFiltro] = useState<FiltroBoard>('all');
  const lista = useMyBoards(filtro);
  const { boards, mioId, statsDi } = lista;
  const { togli } = useTogliBoard();
  const [aperto, setAperto] = useState<BoardView | null>(null);
  const [foglio, setFoglio] = useState(false);

  // L'ora della lista si legge UNA volta per montaggio: «oggi» e «ieri» non cambiano sotto il dito.
  const adesso = useMemo(() => new Date(), []);

  // Il tasto indietro di Telegram: dalla scheda torna alla lista (il foglio ha il suo livello, sopra).
  useLivelloIndietro(aperto !== null, () => setAperto(null));

  if (aperto) {
    return (
      <SchedaAperta
        board={aperto}
        mioId={mioId}
        onIndietro={() => setAperto(null)}
        onSfida={() => setFoglio(true)}
        onApriReplay={(b) => navigate(`/backgammon/replay/${b.origin.matchId}`)}
        onGuarda={(b) => navigate(`/backgammon/live/${b.origin.matchId}`)}
        onTogli={async () => {
          const errore = await togli(aperto.id);
          if (errore === null) setAperto(null);
          return errore;
        }}
        foglio={foglio}
        onChiudiFoglio={() => setFoglio(false)}
      />
    );
  }

  return (
    // `[&>*]:shrink-0`: le righe non si schiacciano una sull'altra quando la lista scorre (misurato nel banco a 320×640).
    <div data-testid="my-boards-lista" className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto py-2 [&>*]:shrink-0">
      {/* I filtri sono scelte accese/spente (`aria-pressed`), non schede: la lista cambia sotto lo stesso titolo. */}
      <div role="group" aria-label={t('myBoards.filtersLabel')} className="flex shrink-0 gap-2 overflow-x-auto pb-1">
        {FILTRI_BOARD.map((f) => {
          const on = f === filtro;
          return (
            <Button
              key={f}
              variant="scelta"
              pressed={on}
              peso="bold"
              data-testid={`my-boards-filtro-${f}`}
              onClick={() => {
                if (on) return;
                haptics.selection();
                setFiltro(f);
              }}
              className="min-h-11 shrink-0 px-4 text-sm"
            >
              {t(`myBoards.filters.${f}`)}
            </Button>
          );
        })}
      </div>

      {lista.stato === 'caricamento' ? (
        <div data-testid="my-boards-carico" className="flex flex-col items-center gap-2 py-8">
          <PanelWaiting slow={false} className="h-auto" />
          <p className="text-sm text-fg-muted">{t('myBoards.loading')}</p>
        </div>
      ) : lista.stato === 'errore' ? (
        <div
          data-testid="my-boards-errore"
          className="flex flex-col items-center gap-3 rounded-xl bg-bg-secondary p-6 text-center"
        >
          <p role="alert" className="text-sm text-danger">
            {t('myBoards.listError')}
          </p>
          <Button variant="neutro" data-testid="my-boards-riprova" className="min-h-11" onClick={lista.riprova}>
            {t('myBoards.retry')}
          </Button>
        </div>
      ) : boards.length === 0 ? (
        <div
          data-testid="my-boards-vuota"
          className="flex flex-col items-center gap-3 rounded-xl bg-bg-secondary p-6 text-center"
        >
          <Bookmark aria-hidden className="h-6 w-6 text-fg-muted" />
          {/* «Tutti» vuoto = nessun board salvato; un filtro vuoto = nessuno in QUEL filtro (la lista e' del server). */}
          <p className="text-sm font-bold text-fg">
            {filtro === 'all' ? t('myBoards.empty.title') : t('myBoards.empty.filter')}
          </p>
          {filtro === 'all' && <p className="text-sm text-fg-muted">{t('myBoards.empty.hint')}</p>}
        </div>
      ) : (
        <>
          {boards.map((b) => (
            <BoardRiga key={b.id} board={b} stats={statsDi(b.position)} mioId={mioId} adesso={adesso} onApri={setAperto} />
          ))}
          {lista.erroreAltre && (
            <p role="alert" data-testid="my-boards-altre-errore" className="text-center text-sm text-danger">
              {t('myBoards.moreError')}
            </p>
          )}
          {lista.haAltre && (
            <Button
              variant="neutro"
              forma="pastiglia"
              data-testid="my-boards-altre"
              disabled={lista.staCaricandoAltre}
              onClick={() => {
                haptics.selection();
                lista.caricaAltre();
              }}
              className="mx-auto min-h-11"
            >
              {lista.erroreAltre ? t('myBoards.retry') : t('myBoards.more')}
            </Button>
          )}
        </>
      )}
    </div>
  );
}

/** La scheda di un board con i suoi dati e, se aperto, il foglio della sfida. Un componente a parte per usare gli hook per board. */
function SchedaAperta({
  board,
  mioId,
  onIndietro,
  onSfida,
  onApriReplay,
  onGuarda,
  onTogli,
  foglio,
  onChiudiFoglio,
}: {
  board: BoardView;
  mioId: string | null;
  onIndietro: () => void;
  onSfida: () => void;
  onApriReplay: (board: BoardView) => void;
  onGuarda: (board: BoardView) => void;
  onTogli: () => Promise<ErroreBoard | null>;
  foglio: boolean;
  onChiudiFoglio: () => void;
}) {
  const dettaglio = useBoardDettaglio(board);
  return (
    <>
      <BoardScheda
        board={board}
        dettaglio={dettaglio}
        mioId={mioId}
        onIndietro={onIndietro}
        onSfida={onSfida}
        onApriReplay={onApriReplay}
        onGuarda={onGuarda}
        onTogli={onTogli}
      />
      {/* 🔴 La sfida e' il T2: a `sfidaDaBoardAcceso()` spento il foglio NON si monta (ne' i suoi amici e tempi finti). */}
      {foglio && dettaglio.scenario && sfidaDaBoardAcceso() && (
        <FoglioSfida board={board} scenario={dettaglio.scenario} onChiudi={onChiudiFoglio} />
      )}
    </>
  );
}

/** Il foglio della sfida coi suoi dati: un componente a parte perche' `useSfidaDaBoard` (T0, amici e tempi FINTI) si chiami solo se il foglio c'e'. */
function FoglioSfida({ board, scenario, onChiudi }: { board: BoardView; scenario: ScenarioView; onChiudi: () => void }) {
  const { amici, modalita } = useSfidaDaBoard();
  return (
    <SfidaDaBoardSheet
      board={board}
      scenario={scenario}
      amici={amici}
      modalita={modalita}
      // T0: nessuna sfida parte. La sfida con la posizione e il colore e' il T2 (Q4, G1, P1).
      onInvia={onChiudi}
      onClose={onChiudi}
    />
  );
}
